import {
  create,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import type { Feature, Point, Polygon, Position } from 'geojson';
import { MAPBOX_ACCESS_TOKEN } from './config';

type Suggestion = {
  feature_name: string;
  matching_name: string;
  description: string;
  language: string;
  action: {
    endpoint: string;
    method: 'POST' | 'GET';
    body: {
      id: string;
    };
    multi_retrievable: boolean;
  };
  maki: string;
  internal_id: string;
  context: {
    layer: string;
    localized_layer: string;
    name: string;
  }[];
  result_type: string[];
  category?: string[];
  category_ids?: string[];
};

const axiosInstance = create({
  baseURL: 'https://api.mapbox.com',
});

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    config.params = {
      ...config.params,
      access_token: MAPBOX_ACCESS_TOKEN,
    };
    return config;
  }
);

const mapboxApiClient = {
  searchMapPlaces: async (
    searchValue: string,
    sessionToken: string
  ): Promise<Suggestion[]> => {
    return axiosInstance
      .get(`/search/v1/suggest/${encodeURIComponent(searchValue)}`, {
        params: {
          session_token: sessionToken,
          limit: 10,
          language: 'ja',
          country: 'JP',
        },
      })
      .then(
        (
          response: AxiosResponse<{ suggestions: Suggestion[] }>
        ): Suggestion[] => {
          return response.data.suggestions;
        }
      )
      .catch((error: unknown): Suggestion[] => {
        console.error('Failed to fetch map place suggestions:', error);
        return [];
      });
  },
  retrieveSearchResult: async (
    placeId: string,
    sessionToken: string
  ): Promise<Position | null> => {
    return axiosInstance
      .post(
        `/search/v1/retrieve`,
        { id: placeId },
        {
          params: {
            session_token: sessionToken,
          },
        }
      )
      .then(
        (
          response: AxiosResponse<{ features: Feature<Point>[] }>
        ): Position | null => {
          const geometry = response.data.features[0]?.geometry;
          return geometry?.type === 'Point' ? geometry.coordinates : null;
        }
      )
      .catch((error: unknown): Position | null => {
        console.error('Failed to retrieve map place:', error);
        return null;
      });
  },
  getIsochrone: async (
    point: Position,
    minutes: number,
    profile: 'driving' | 'walking' | 'cycling'
  ): Promise<Polygon | null> => {
    return axiosInstance
      .get(`/isochrone/v1/mapbox/${profile}/${point[0]},${point[1]}`, {
        params: {
          contours_minutes: minutes,
          polygons: true,
        },
      })
      .then(
        (
          response: AxiosResponse<{ features: Feature<Polygon>[] }>
        ): Polygon | null => {
          const geometry = response.data.features[0]?.geometry;
          return geometry?.type === 'Polygon' ? geometry : null;
        }
      )
      .catch((error: unknown): null => {
        console.error('Failed to fetch isochrone data:', error);
        return null;
      });
  },
};

export type { Suggestion };
export { mapboxApiClient };
